/**
 * Tipos de registro (ITIL) de un ticket.
 *
 * Valores persistidos en la columna `Tickets.ticket.type`:
 *   Soporte (ITIL): incident · request · problem · change
 *   Desarrollo:     bug · feature · improvement · task
 *
 * Cada tipo pertenece a una familia (`group`) para poder ofrecerlos en la UI en
 * dos niveles: primero la familia, luego el tipo concreto.
 *
 * Es un conjunto fijo (no editable desde la UI), por eso vive como configuración
 * en código en lugar de una tabla de catálogo.
 */

/** Familias de tipos de registro (primer nivel de selección). */
export const TICKET_GROUPS = {
  support: {
    label: 'Soporte',
    icon: 'fa-headset',
    color: '#3b82f6',
    hint: 'Algo que ya existe y no funciona, o un servicio que necesita',
  },
  development: {
    label: 'Desarrollo',
    icon: 'fa-code',
    color: '#8b5cf6',
    hint: 'Trabajo sobre nuestros sistemas: defectos, funciones y tareas',
  },
}

export const TICKET_TYPES = {
  incident: {
    group: 'support',
    label: 'Incidente',
    icon: 'fa-triangle-exclamation',
    color: '#ef4444',
    hint: 'Algo no funciona o está degradado',
    examples: [
      'No puedo iniciar sesión en Bitrix24',
      'La impresora de contabilidad no responde',
      'El módulo de facturación tarda más de un minuto en cargar',
    ],
  },
  request: {
    group: 'support',
    label: 'Solicitud',
    icon: 'fa-inbox',
    color: '#3b82f6',
    hint: 'Petición de un servicio, acceso o recurso',
    examples: [
      'Necesito acceso a la carpeta compartida de Proyectos',
      'Solicito una laptop para el nuevo colaborador',
      'Crear un correo institucional para un ingreso',
    ],
  },
  problem: {
    group: 'support',
    label: 'Problema',
    icon: 'fa-magnifying-glass-chart',
    color: '#a855f7',
    hint: 'Causa raíz detrás de uno o varios incidentes',
    examples: [
      'Cada lunes se cae el acceso a la VPN en toda la sucursal',
      'Varios usuarios reportan el mismo error al exportar reportes',
      'El servidor se reinicia sin causa aparente desde hace semanas',
    ],
  },
  change: {
    group: 'support',
    label: 'Cambio',
    icon: 'fa-code-branch',
    color: '#14b8a6',
    hint: 'Modificación planificada de un sistema',
    examples: [
      'Actualizar la versión del CMS el próximo fin de semana',
      'Agregar un campo nuevo al formulario de OPS',
      'Migrar el respaldo de archivos a otro almacenamiento',
    ],
  },
  bug: {
    group: 'development',
    label: 'Bug',
    icon: 'fa-bug',
    color: '#f97316',
    hint: 'Defecto reproducible en un sistema propio',
    examples: [
      'El total de la factura suma mal cuando hay descuento',
      'El botón «Guardar» no hace nada en la vista de proyectos',
      'La fecha se muestra un día antes en el listado de tickets',
    ],
  },
  feature: {
    group: 'development',
    label: 'Funcionalidad',
    icon: 'fa-wand-magic-sparkles',
    color: '#22c55e',
    hint: 'Algo nuevo que hoy no existe en el sistema',
    examples: [
      'Agregar un reporte de horas por colaborador en KPIS',
      'Que Tickets permita adjuntar archivos en los comentarios',
      'Crear una pantalla de aprobación de vacaciones',
    ],
  },
  improvement: {
    group: 'development',
    label: 'Mejora',
    icon: 'fa-arrow-trend-up',
    color: '#0ea5e9',
    hint: 'Optimizar o ampliar algo que ya funciona',
    examples: [
      'Que el listado de tickets cargue más rápido',
      'Agregar un filtro por sucursal al reporte existente',
      'Simplificar el formulario de creación en el celular',
    ],
  },
  task: {
    group: 'development',
    label: 'Tarea técnica',
    icon: 'fa-list-check',
    color: '#64748b',
    hint: 'Trabajo interno de TI, sin pedido de usuario',
    examples: [
      'Depurar registros antiguos de la base de datos',
      'Renovar el certificado SSL antes de su vencimiento',
      'Documentar el proceso de despliegue del CMS',
    ],
  },
}

/** Tipo usado por defecto cuando un ticket no tiene tipo. */
export const DEFAULT_TICKET_TYPE = 'incident'

/** Lista para iterar en selectores: [{ value, group, label, icon, color, hint, examples }]. */
export const TICKET_TYPE_LIST = Object.entries(TICKET_TYPES).map(
  ([value, meta]) => ({ value, ...meta })
)

/**
 * Árbol de dos niveles para la UI: familia → tipos que contiene.
 * Se deriva del catálogo, de modo que basta agregar un tipo con su `group`
 * para que aparezca en todos los selectores.
 *
 * @type {Array<{value: string, label: string, icon: string, color: string,
 *   hint: string, types: Array<Object>}>}
 */
export const TICKET_TYPE_TREE = Object.entries(TICKET_GROUPS).map(
  ([value, meta]) => ({
    value,
    ...meta,
    types: TICKET_TYPE_LIST.filter((type) => type.group === value),
  })
)

/**
 * Devuelve los metadatos de un tipo de registro.
 * @param {string} value
 * @returns {{group: string, label: string, icon: string, color: string, hint: string}}
 */
export const getTicketType = (value) =>
  TICKET_TYPES[value] || TICKET_TYPES[DEFAULT_TICKET_TYPE]

/**
 * Devuelve la familia a la que pertenece un tipo de registro.
 * @param {string} value - Valor del tipo (no de la familia).
 * @returns {{label: string, icon: string, color: string, hint: string}}
 */
export const getTicketGroup = (value) =>
  TICKET_GROUPS[getTicketType(value).group]
